<template>
  <div class="ms-field-wrap">
    <label v-if="label" class="form-label form-label-thin">{{ label }}</label>

    <div class="ms-wrap" :class="{ open: isOpen }" ref="wrapRef" @click.self="toggleOpen">
      <div class="ms-inner" @click="toggleOpen">
        <template v-if="!multiple">
          <span v-if="modelValue === '' || modelValue === null || modelValue === undefined" class="ms-placeholder">{{ placeholder }}</span>
          <span v-else class="ms-single-value">{{ labelFor(modelValue) }}</span>
        </template>
        <template v-else>
          <span v-if="!modelValue.length" class="ms-placeholder">{{ placeholder }}</span>
          <span v-for="id in modelValue" :key="id" class="ms-tag">
            {{ labelFor(id) }}
            <span class="ms-tag-x" @click.stop="remove(id)">✕</span>
          </span>
        </template>
      </div>

      <span
        v-if="!multiple && modelValue !== '' && modelValue !== null && modelValue !== undefined"
        class="ms-clear"
        @click.stop="clearSingle"
        title="Clear"
      >✕</span>
      <i class="fa fa-chevron-down ms-chevron" aria-hidden="true"></i>
    </div>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="ms-dropdown-portal"
        :style="dropdownStyle"
        ref="dropdownRef"
      >
        <div class="ms-search-wrap">
          <input
            v-model="query"
            ref="searchRef"
            type="text"
            :placeholder="`Search ${placeholder.toLowerCase()}…`"
            autocomplete="off"
            @click.stop
          />
        </div>
        <div v-if="multiple" class="ms-select-all-row">
          <span>{{ modelValue.length }} selected</span>
          <button type="button" @click.stop="clearAll">Clear all</button>
        </div>
        <ul class="ms-opts" role="listbox" :aria-multiselectable="multiple">
          <li v-if="!hasResults" class="ms-empty">No results</li>

          <!-- GROUPED mode -->
          <template v-if="isGrouped">
            <template v-for="group in filteredGroups" :key="group.label">
              <li class="ms-group-label">{{ group.label }}</li>
              <li
                v-for="opt in group.options"
                :key="opt.value"
                class="ms-opt ms-opt-indented"
                :class="{ selected: isSelected(opt.value) }"
                role="option"
                :aria-selected="isSelected(opt.value)"
                @click.stop="toggle(opt.value)"
              >
                <span class="ms-check">{{ isSelected(opt.value) ? '✓' : '' }}</span>
                {{ opt.label }}
              </li>
            </template>
          </template>

          <!-- FLAT mode (existing behaviour) -->
          <template v-else>
            <li
              v-for="opt in filteredOptions"
              :key="opt.value"
              class="ms-opt"
              :class="{ selected: isSelected(opt.value) }"
              role="option"
              :aria-selected="isSelected(opt.value)"
              @click.stop="toggle(opt.value)"
            >
              <span class="ms-check">{{ isSelected(opt.value) ? '✓' : '' }}</span>
              {{ opt.label }}
            </li>
          </template>
        </ul>
      </div>
    </Teleport>

    <!-- Hidden slot for <option>/<optgroup> fallback -->
    <div ref="slotRef" style="display:none">
      <slot />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  modelValue:   { default: () => [] },
  placeholder:  { type: String, default: 'Select…' },
  label:        { type: String, default: '' },
  multiple:     { type: Boolean, default: true },

  // Flat options: [{ value, label }] or [{ id, name }] etc.
  options:      { type: Array, default: null },

  // Grouped options: [{ label: 'Category', options: [{ value, label }] }]
  // OR the raw shape from your API: [{ id, name, get_materials: [...] }]
  groups:       { type: Array, default: null },

  // Keys used when groups come in the raw API shape
  groupLabelKey:   { type: String, default: 'name' },
  groupChildrenKey:{ type: String, default: 'options' }, // or 'get_materials', etc.
  childValueKey:   { type: String, default: 'value' },   // or 'id'
  childLabelKey:   { type: String, default: 'label' },   // or 'name'

  // Keys for flat options
  labelKey:  { type: String, default: 'name' },
  valueKey:  { type: String, default: 'id' },
})

const emit = defineEmits(['update:modelValue'])

const isOpen     = ref(false)
const query      = ref('')
const wrapRef    = ref(null)
const searchRef  = ref(null)
const slotRef    = ref(null)
const dropdownRef  = ref(null)
const dropdownStyle = ref({})
const slotOptions  = ref([])
const slotGroups   = ref([])

// ── Are we in grouped mode? ────────────────────────────────────────────────
const isGrouped = computed(() => {
  return (props.groups !== null && props.groups.length > 0) || slotGroups.value.length > 0
})

// ── Normalise groups into [{ label, options: [{value, label}] }] ───────────
const parsedGroups = computed(() => {
  if (props.groups !== null) {
    return props.groups.map(g => {
      // Already normalised shape: { label, options }
      if (Array.isArray(g.options)) {
        return {
          label: g.label ?? g[props.groupLabelKey] ?? '',
          options: g.options.map(o => ({
            value: o.value ?? o[props.childValueKey],
            label: o.label ?? o[props.childLabelKey] ?? '',
          })),
        }
      }
      // Raw API shape: { name, get_materials: [...] }
      const children = g[props.groupChildrenKey] ?? []
      return {
        label: g[props.groupLabelKey] ?? '',
        options: children.map(o => ({
          value: o.value ?? o[props.childValueKey] ?? o.id,
          label: o.label ?? o[props.childLabelKey] ?? o.name ?? '',
        })),
      }
    })
  }
  return slotGroups.value
})

// Flat list of all options across all groups (for labelFor / isSelected lookup)
const allGroupOptions = computed(() =>
  parsedGroups.value.flatMap(g => g.options)
)

// ── Normalise flat options ─────────────────────────────────────────────────
const parsedOptions = computed(() => {
  if (props.options !== null) {
    return props.options.map(o => ({
      value: o.value  ?? o[props.valueKey],
      label: o.label  ?? o[props.labelKey] ?? '',
    }))
  }
  return slotOptions.value
})

// ── Filtering ──────────────────────────────────────────────────────────────
const filteredGroups = computed(() => {
  const q = query.value.toLowerCase()
  if (!q) return parsedGroups.value
  return parsedGroups.value
    .map(g => ({
      ...g,
      options: g.options.filter(o => o.label.toLowerCase().includes(q)),
    }))
    .filter(g => g.options.length > 0)
})

const filteredOptions = computed(() => {
  const q = query.value.toLowerCase()
  return parsedOptions.value.filter(o => o.label.toLowerCase().includes(q))
})

const hasResults = computed(() =>
  isGrouped.value ? filteredGroups.value.length > 0 : filteredOptions.value.length > 0
)

// ── Lookup helpers ─────────────────────────────────────────────────────────
const labelFor = (val) => {
  if (isGrouped.value) {
    return allGroupOptions.value.find(o => o.value === val)?.label ?? val
  }
  return parsedOptions.value.find(o => o.value === val)?.label ?? val
}
const isSelected = (val) =>
  props.multiple ? props.modelValue.includes(val) : props.modelValue === val

// ── Toggle / clear ─────────────────────────────────────────────────────────
function toggle(val) {
  if (!props.multiple) {
    emit('update:modelValue', val)
    isOpen.value = false
    query.value  = ''
  } else {
    const next = isSelected(val)
      ? props.modelValue.filter(v => v !== val)
      : [...props.modelValue, val]
    emit('update:modelValue', next)
  }
}
function remove(val)   { emit('update:modelValue', props.modelValue.filter(v => v !== val)) }
function clearAll()    { emit('update:modelValue', []) }
function clearSingle() { emit('update:modelValue', '') }

// ── Dropdown positioning ───────────────────────────────────────────────────
function calcDropdownPosition() {
  if (!wrapRef.value) return
  const rect = wrapRef.value.getBoundingClientRect()
  dropdownStyle.value = {
    position: 'fixed',
    top:   rect.bottom + 4 + 'px',
    left:  rect.left   + 'px',
    width: rect.width  + 'px',
    zIndex: 9999,
  }
}
async function toggleOpen() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    calcDropdownPosition()
    await nextTick()
    searchRef.value?.focus()
  }
}
function onOutsideClick(e) {
  if (wrapRef.value?.contains(e.target) || dropdownRef.value?.contains(e.target)) return
  isOpen.value = false
}
function onScroll() {
  if (isOpen.value) calcDropdownPosition()
}

// ── Slot parsing (<option> / <optgroup>) ──────────────────────────────────
function parseSlotOptions() {
  if (!slotRef.value) return

  const optgroups = slotRef.value.querySelectorAll('optgroup')
  if (optgroups.length) {
    slotGroups.value = Array.from(optgroups).map(grp => ({
      label: grp.label,
      options: Array.from(grp.querySelectorAll('option'))
        .filter(el => el.value !== '')
        .map(el => ({ value: el.value, label: el.textContent.trim() })),
    }))
    return
  }

  // No optgroups — fall back to flat options
  const nodes = slotRef.value.querySelectorAll('option')
  slotOptions.value = Array.from(nodes)
    .filter(el => el.value !== '')
    .map(el => ({ value: el.value, label: el.textContent.trim() }))
}

onMounted(() => {
  parseSlotOptions()
  document.addEventListener('click', onOutsideClick)
  window.addEventListener('scroll', onScroll, true)
  window.addEventListener('resize', onScroll)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onOutsideClick)
  window.removeEventListener('scroll', onScroll, true)
  window.removeEventListener('resize', onScroll)
})
</script>

<style scoped>
.ms-field-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ms-wrap {
  position: relative;
  background: #fff;
  border: 0.5px solid rgba(0,0,0,.25);
  border-radius: 0.2rem !important;
  border: 1px solid #ced4da !important;
  min-height: 32px;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s;
}
.ms-wrap.open,
.ms-wrap:focus-within {
  border-color: rgba(0,0,0,.4);
  box-shadow: 0 0 0 3px rgba(83,74,183,.12);
}

.ms-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding: 3px 56px 3px 8px;
  min-height: 32px;
}

.ms-placeholder { font-size: 13px; color: #aaa; }
.ms-single-value { font-size: 13px; color: #333; line-height: 1; }

.ms-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #EEEDFE;
  color: #3C3489;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 6px 2px 8px;
  border-radius: 20px;
  border: 1px solid rgba(83,74,183,.35);
  line-height: 1.2;
}
.ms-tag-x {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px; height: 12px;
  border-radius: 50%;
  background: rgba(83,74,183,.15);
  cursor: pointer;
  font-size: 9px;
}
.ms-tag-x:hover { background: rgba(83,74,183,.3); }

.ms-chevron {
  position: absolute;
  right: 10px; top: 50%;
  transform: translateY(-50%);
  color: #aaa; font-size: 14px;
  pointer-events: none;
  transition: transform .2s;
}
.ms-wrap.open .ms-chevron { transform: translateY(-50%) rotate(180deg); }

.ms-clear {
  position: absolute;
  right: 30px; top: 50%;
  transform: translateY(-50%);
  color: #bbb; font-size: 11px; cursor: pointer;
  width: 16px; height: 16px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
}
.ms-clear:hover { color: #555; background: rgba(0,0,0,.06); }
</style>

<!-- Portal dropdown — must NOT be scoped so Teleport can pick it up -->
<style>
.ms-dropdown-portal {
  background: #fff;
  border: 0.5px solid rgba(0,0,0,.2);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,.08);
  overflow: hidden;
}
.ms-dropdown-portal .ms-search-wrap {
  padding: 8px 10px;
  border-bottom: 0.5px solid rgba(0,0,0,.1);
}
.ms-dropdown-portal .ms-search-wrap input {
  width: 100%; border: none; outline: none;
  font-size: 13px; background: transparent;
}
.ms-dropdown-portal .ms-select-all-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 6px 12px; font-size: 12px; color: #888;
  border-bottom: 0.5px solid rgba(0,0,0,.07);
}
.ms-dropdown-portal .ms-select-all-row button {
  background: none; border: none; color: #534AB7; cursor: pointer; font-size: 12px;
}
.ms-dropdown-portal .ms-opts {
  list-style: none; margin: 0; padding: 0;
  max-height: 220px; overflow-y: auto;
}

/* Group label row */
.ms-dropdown-portal .ms-group-label {
  padding: 6px 12px 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .5px;
  color: #9ca3af;
  background: #f9fafb;
  border-bottom: 0.5px solid rgba(0,0,0,.06);
  cursor: default;
  user-select: none;
}

/* Normal option */
.ms-dropdown-portal .ms-opt {
  display: flex; align-items: center;
  gap: 10px; padding: 8px 12px;
  font-size: 13px; cursor: pointer;
}
/* Indented option inside a group */
.ms-dropdown-portal .ms-opt-indented {
  padding-left: 20px;
}
.ms-dropdown-portal .ms-opt:hover { background: #f7f7f5; }
.ms-dropdown-portal .ms-opt.selected { color: #3C3489; background: #EEEDFE; }

.ms-dropdown-portal .ms-check {
  width: 16px; height: 16px; border-radius: 4px;
  border: 1.5px solid rgba(0,0,0,.2);
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; flex-shrink: 0;
}
.ms-dropdown-portal .ms-opt.selected .ms-check {
  background: #534AB7; border-color: #534AB7; color: #fff;
}
.ms-dropdown-portal .ms-empty {
  padding: 16px; font-size: 13px; color: #aaa; text-align: center;
}
</style>